import {
  CreateProjectFromTemplateBody,
  ProjectTemplate,
  ProjectTemplateCreateBody,
  ProjectTemplateVersionCreateBody,
} from '@/types/project-templates'
import useCreateDetail, {
  UseCreateDetailOptions,
} from '../core-hooks/useCreateDetail'
import useGetDetail, { UseGetDetailOptions } from '../core-hooks/useGetDetail'
import useGetList, { UseGetListOptions } from '../core-hooks/useGetList'
import useUpdateDetail, {
  UseUpdateDetailOptions,
} from '../core-hooks/useUpdateDetail'
import buildService from '../create-service'
import queryKeys from '../query-keys'
import { createDetail } from '../request-wrappers'

const service = buildService<ProjectTemplate>({
  pathCategory: 'project_templates',
})

export function useGetProjectTemplate({
  id,
  ...options
}: UseGetDetailOptions<ProjectTemplate>) {
  const query = useGetDetail<ProjectTemplate>({
    id,
    queryKey: queryKeys.projectTemplates.detail({
      id,
      filters: options.filters,
    }),
    serviceFn: service.getDetail,
    ...options,
  })

  return {
    projectTemplate: query.data,
    ...query,
  }
}

export function useGetProjectTemplates({
  filters,
  ...options
}: UseGetListOptions<ProjectTemplate> = {}) {
  const query = useGetList<ProjectTemplate>({
    filters,
    queryKey: queryKeys.projectTemplates.list({ filters }),
    serviceFn: service.getList,
    ...options,
  })

  return {
    projectTemplates: query.allData,
    ...query,
  }
}

export function useUpdateProjectTemplate(
  options: UseUpdateDetailOptions<ProjectTemplate> = {},
) {
  const mutation = useUpdateDetail<ProjectTemplate>({
    serviceFn: service.updateDetail,
    ...options,
  })

  return {
    updateProjectTemplate: mutation.mutate,
    updateProjectTemplateAsync: mutation.mutateAsync,
    ...mutation,
  }
}

/**
 * MARK: Action Hook: Create Project Template
 * Creates a new project template.
 */
export function useCreateProjectTemplate(
  options: UseCreateDetailOptions<ProjectTemplateCreateBody> = {},
) {
  const mutation = useCreateDetail<ProjectTemplateCreateBody>({
    serviceFn: (serviceFnOptions) => {
      return createDetail({
        url: '/v2/template/projects/create',
        ...serviceFnOptions,
      })
    },
    ...options,
  })

  return {
    createProjectTemplate: mutation.mutate,
    createProjectTemplateAsync: mutation.mutateAsync,
    ...mutation,
  }
}

/**
 * MARK: Action Hook: Create Project Template Version
 * Creates a new project template version.
 */
export function useCreateProjectTemplateVersion(
  options: UseCreateDetailOptions<ProjectTemplateVersionCreateBody> = {},
) {
  const mutation = useCreateDetail<ProjectTemplateVersionCreateBody>({
    serviceFn: (serviceFnOptions) => {
      return createDetail({
        url: '/v2/template/projects/create-version',
        ...serviceFnOptions,
      })
    },
    ...options,
  })

  return {
    createProjectTemplateVersion: mutation.mutate,
    createProjectTemplateVersionAsync: mutation.mutateAsync,
    ...mutation,
  }
}

/**
 * MARK: Action Hook: Create Project From Template
 * Creates a new project from a template.
 */
export function useCreateProjectFromTemplate(
  options: UseCreateDetailOptions<
    CreateProjectFromTemplateBody,
    { id: string }
  > = {},
) {
  const mutation = useCreateDetail<
    CreateProjectFromTemplateBody,
    { id: string }
  >({
    serviceFn: (serviceFnOptions) => {
      return createDetail({
        url: '/v2/template/projects/create-from-template',
        ...serviceFnOptions,
      })
    },
    ...options,
  })

  return {
    createProjectFromTemplate: mutation.mutate,
    createProjectFromTemplateAsync: mutation.mutateAsync,
    ...mutation,
  }
}
